/**
 * The Field Manual. Rich text: `**strong**`, `*emphasis*`, `[[term]]` (small-caps term) — see
 * ui/common/RichText.tsx.
 */
export const manual = {
  'fm.kicker': 'Instruction for the conduct of operations',
  'fm.title': 'Field Manual',
  'fm.chapters': 'Chapters',
  'fm.ch.command': 'Command',
  'fm.ch.fog': 'Fog of war',
  'fm.ch.combat': 'Combat',
  'fm.ch.supply': 'Supply & Kessel',
  'fm.ch.weather': 'Weather',
  'fm.ch.economy': 'Economy',
  'fm.ch.victory': 'Victory',
  'fm.ch.keys': 'Hotkeys',
  'fm.academy.kicker': 'Rather learn by doing?',
  'fm.academy.title': 'Kriegsakademie',
  'fm.academy.text': 'Fourteen short lessons on a quiet front, with an instructor.',
  'fm.docHead': 'FIELD MANUAL F.M. 100-5 (FRICTION)',
  'fm.docClass': 'FOR OFFICIAL USE',

  'fm.command.h': 'Command & couriers',
  'fm.command.p':
    'You never move a division directly. You **write orders** — draw them on the map — and those orders must travel from the nearest headquarters to the unit. Until they arrive, the unit keeps doing what it was last told.',
  'fm.command.fig.phone': 'TELEPHONE · 1 h',
  'fm.command.fig.courier': 'COURIER · CAN BE CAPTURED',
  'fm.command.caption':
    "Inside an HQ's **command radius** (3 hexes) orders go by field telephone and arrive in an hour. Beyond it a courier rides the road net — visible on the map and capturable. With *Field Radio* orders arrive anywhere in an hour, but enemy codebreakers may read them.",
  'fm.command.li1':
    '[[Move]] marches without seeking a fight. [[Attack]] advances and assaults. [[Hold]] stops and digs in. [[Retreat]] breaks contact at a cost in organisation.',
  'fm.command.li2':
    '[[Postures]] are standing orders for when couriers are late: aggressive units counter-attack weak neighbours, balanced ones hold, defensive ones fall back before they shatter.',
  'fm.command.li3':
    '[[H-hour]]: give several units the same execution time and they jump off together — if their couriers arrive in time.',
  'fm.command.li4':
    'Lose all your HQs and orders leave from the capital, slowly. Protect yours; hunt theirs.',

  'fm.fog.h': 'Fog of war & intelligence',
  'fm.fog.p':
    'Units see two hexes (recon four, more from hills). Forests, towns and mountains hide their occupants from anyone not adjacent. What you know about the enemy comes in three grades:',
  'fm.fog.contact': 'Contact',
  'fm.fog.identified': 'Identified',
  'fm.fog.assessed': 'Assessed',
  'fm.fog.unmasked': 'Decoy unmasked',
  'fm.fog.caption':
    'A **contact** is just “something there”. **Identified** gives type and designation — decoys still pass. **Assessed** (adjacent) reveals strength and organisation, and unmasks decoys.',
  'fm.fog.li1':
    'Enemies you stop seeing become ghosts with a “last seen” age. After four days they are forgotten.',
  'fm.fog.li2':
    'Territory outside your sight shows its last known owner — the front may have moved without you knowing.',
  'fm.fog.li3':
    "Captured couriers and decrypted radio traffic appear as red INTERCEPTED arrows: the enemy's own plan.",

  'fm.combat.h': 'Combat & flanking',
  'fm.combat.fig.defender': 'defender',
  'fm.combat.caption':
    'Each extra direction of attack adds **+15 %** (max +45 %). Attacking across a river costs 30 %. Artillery within two hexes joins every friendly battle.',
  'fm.combat.p':
    'A battle is fought hour by hour: attack power against defence, shaped by terrain, rivers, entrenchment, supply, organisation, night and flanking. The loser bleeds **organisation** fast and strength slowly. A defender whose organisation collapses retreats — and if there is nowhere to go, it surrenders.',
  'fm.combat.col.terrain': 'Terrain',
  'fm.combat.col.move': 'Move',
  'fm.combat.col.defence': 'Defence',
  'fm.combat.col.notes': 'Notes',
  'fm.combat.note.plains': 'Tank country',
  'fm.combat.note.forest': 'Conceals; armor −30 %',
  'fm.combat.note.hills': '+1 sight',
  'fm.combat.note.mountains': 'Conceals; armor −50 %',
  'fm.combat.note.marsh': 'Armor −45 %',
  'fm.combat.note.urban': 'Conceals; armor −40 %',
  'fm.combat.night': 'Night (20:00–05:00): sight −1, marching 30 % slower, attacks −20 %.',

  'fm.supply.h': 'Supply & the Kessel',
  'fm.supply.p1':
    'Supply flows from your capital and cities through your own territory, further along roads and railways. Units out of supply attack weakly and stop recovering; after two days at zero with broken organisation they surrender.',
  'fm.supply.p2':
    "Cut an enemy's lines and the pocket is announced on the teletype: *“KESSEL — 3 divisions encircled near Dornheim”*. Hold the ring. Encircled units of yours should break out at once — or be relieved.",

  'fm.weather.h': 'Weather & seasons',
  'fm.weather.p1':
    'Every campaign has its season. Fronts drift in from the west, summer afternoons brew thunderstorms, autumn rain turns the fields to mud, winter freezes rivers and marshes hard. The Meteorological Service (*W*) issues a 24-hour outlook every six hours — sharp for the next hours, a guess for tomorrow.',
  'fm.weather.li1':
    '**Rain**: marching 20 % slower, attacks −10 %, sight −1, no air photos. **Storm**: marching 40 % slower, attacks −20 %, couriers slower.',
  'fm.weather.li2':
    '**Mud** (off the roads): infantry 40 % slower, tanks and lorries half speed and −20 % in the attack, supply dearer. Roads ignore it — hold them.',
  'fm.weather.li3':
    '**Fog** (mornings, low wet ground): sight −2, units hidden unless adjacent, attackers +10 %, couriers slip through. Forecast the evening before.',
  'fm.weather.li4':
    '**Snow** slows movement off the roads and makes starvation worse; **frost** freezes rivers (no bridge needed) and marshes (they bear like plains).',
  'fm.weather.p2': 'Choose the day of the offensive: wait out the storm, strike out of the fog.',

  'fm.economy.h': 'Economy & research',
  'fm.economy.li1':
    'Cities in supply produce **industry** and **manpower**. Up to four production items are built in parallel; finished divisions appear at the chosen city.',
  'fm.economy.li2':
    'Research points come from a base rate plus 12 % of industry. Four branches: Signals, Intelligence, Warfare, Logistics.',
  'fm.economy.li3':
    'Radio makes command fast but leaky; cryptanalysis reads theirs; rotor ciphers protect yours; decoys waste their attention.',

  'fm.victory.h': 'Victory',
  'fm.victory.li1': 'Cities are worth victory points (town 1, city 3, metropolis 6, capital +4).',
  'fm.victory.li2': 'A team holding the victory share of all VP (usually 60 %) for 24 hours wins outright.',
  'fm.victory.li3':
    'A nation that has lost its capital and holds under a quarter of its starting VP capitulates.',
  'fm.victory.li4': 'With a day limit, the most VP when time runs out wins. Last team standing always wins.',

  'fm.keys.h': 'Hotkeys',
} as const;
